import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone, MapPin, ArrowRight } from "lucide-react";
import LogoText from "./LogoText";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { useActiveTransferDestinations } from "@/hooks/useTransferDestinations";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { getConfig } = useSiteConfig();
  const { data: destinations = [] } = useActiveTransferDestinations();
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const popularDestinations = destinations
    .sort((a, b) => a.display_order - b.display_order)
    .slice(0, 6);

  const navigationItems = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Transfers", href: "/transfers" },
    { name: "Destinations", href: "/traslados" },
    { name: "Contact", href: "/contact" },
  ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-[hsl(214_84%_25%/0.92)] backdrop-blur-xl shadow-lg border-b border-white/10' 
        : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 hover:opacity-90 transition-opacity">
            <LogoText className="h-14" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <NavigationMenu>
              <NavigationMenuList className="gap-1">
                {navigationItems.map((item) => {
                  if (item.name === "Destinations") {
                    return (
                      <NavigationMenuItem key={item.name}>
                        <NavigationMenuTrigger 
                          className={`font-medium transition-all text-sm uppercase tracking-wider bg-transparent px-4 py-2 rounded-full ${
                          isScrolled
                            ? isActive(item.href) ? "text-secondary bg-white/10" : "text-white/90 hover:text-white hover:bg-white/10"
                            : isActive(item.href) ? "text-secondary bg-white/10" : "text-white/90 hover:text-white hover:bg-white/10"
                          }`}
                        >
                          {item.name}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent className="z-50">
                          <div className="w-[500px] glass rounded-2xl p-6">
                            <div className="mb-4">
                              <h3 className="text-lg font-bold text-foreground mb-1">Popular Destinations</h3>
                              <p className="text-sm text-muted-foreground">Most requested transfer routes</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              {popularDestinations.map((destination) => (
                                <Link
                                  key={destination.id}
                                  to={`/traslados/${destination.slug}`}
                                  className="group flex items-start gap-3 p-3 rounded-xl hover:bg-primary/10 transition-all"
                                >
                                  <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-foreground group-hover:text-primary transition-colors text-sm">
                                      {destination.name}
                                    </div>
                                    {destination.from_airport_price && (
                                      <div className="text-xs text-muted-foreground mt-0.5">
                                        From ${destination.from_airport_price}
                                      </div>
                                    )}
                                  </div>
                                </Link>
                              ))}
                            </div>
                            <Link
                              to="/traslados"
                              className="mt-4 flex items-center justify-center gap-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                            >
                              View All Destinations <ArrowRight className="h-4 w-4" />
                            </Link>
                          </div>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    );
                  }
                  
                  return (
                    <NavigationMenuItem key={item.name}>
                      <Link
                        to={item.href}
                        className={`font-medium transition-all text-sm uppercase tracking-wider px-4 py-2 rounded-full inline-block ${
                        isScrolled
                            ? isActive(item.href) ? "text-secondary bg-white/10" : "text-white/90 hover:text-white hover:bg-white/10"
                            : isActive(item.href) ? "text-secondary bg-white/10" : "text-white/90 hover:text-white hover:bg-white/10"
                        }`}
                      >
                        {item.name}
                      </Link>
                    </NavigationMenuItem>
                  );
                })}
              </NavigationMenuList>
            </NavigationMenu>
          </nav>

          {/* Contact Info & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <a href={`tel:${getConfig('contact_phone', '+1 (809) 123-4567')}`} className="flex items-center space-x-2 text-sm transition-colors text-white/80 hover:text-white">
              <Phone className="h-4 w-4" />
              <span className="font-medium">{getConfig('contact_phone', '+1 (809) 123-4567')}</span>
            </a>
            <Link to="/transfers">
              <Button className="bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-secondary rounded-full px-6 transition-all duration-300 hover:scale-105 font-semibold">
                Book Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] bg-gradient-to-b from-primary to-primary/90 text-primary-foreground border-0 p-0">
              <div className="flex flex-col h-full p-6">
                <div className="mb-8">
                  <LogoText className="h-12" useFooterLogo />
                </div>
                
                <nav className="flex flex-col space-y-2 flex-1">
                  {navigationItems.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={`font-medium text-lg px-4 py-3 rounded-xl transition-all ${
                        isActive(item.href) 
                          ? "bg-white/20 text-white" 
                          : "text-white/80 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </nav>

                <div className="space-y-4 pt-6 border-t border-white/20">
                  <a href={`tel:${getConfig('contact_phone', '+1 (809) 123-4567')}`} className="flex items-center space-x-2 text-sm text-white/80">
                    <Phone className="h-4 w-4" />
                    <span>{getConfig('contact_phone', '+1 (809) 123-4567')}</span>
                  </a>
                  <Link to="/transfers" onClick={() => setIsOpen(false)}>
                    <Button className="bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-lg w-full rounded-full font-semibold">
                      Book Now
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
